import React, { useState, useEffect } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useToast } from "@/hooks/use-toast"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { AuthLayout } from "@/components/auth/components/AuthLayout"
import {
  checkPasswordResetCode,
  resetPasswordWithCode,
} from "@/services/auth/passwordReset"
import {
  getInvitationByEmail,
  updateInvitationStatus,
} from "@/services/auth/userManagement"
import { Member, MemberStatus } from "@/types/schema"
import { Lock, ArrowLeft } from "lucide-react"
import { getAuth, verifyPasswordResetCode } from "firebase/auth"
import { db } from "@/utils/firebase"
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore"

const PasswordResetConfirm: React.FC = () => {
  const [searchParams] = useSearchParams()
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [isCodeValid, setIsCodeValid] = useState(false)
  const { toast } = useToast()
  const navigate = useNavigate()

  // Extract oobCode and mode from Firebase URL structure
  const mode = searchParams.get("mode")
  const resetCode = searchParams.get("oobCode")

  useEffect(() => {
    const validateResetCode = async () => {
      // Check if this is indeed a password reset link
      if (mode !== "resetPassword" || !resetCode) {
        toast({
          title: "Invalid Request",
          description: "This doesn't appear to be a valid password reset link.",
          variant: "destructive",
        })
        navigate("/reset-password")
        return
      }

      // Verify the reset code
      const result = await checkPasswordResetCode(resetCode)
      if (!result.success) {
        toast({
          title: "Invalid Reset Link",
          description:
            result.error ||
            "The password reset link is invalid or has expired.",
          variant: "destructive",
        })
        navigate("/reset-password")
      } else {
        setIsCodeValid(true)
      }
    }

    validateResetCode()
  }, [resetCode, mode, navigate, toast])

  // Helper function to update a member's status in a portfolio
  const updateMemberStatusInPortfolio = async (
    userId: string,
    portfolioId: string,
    status: string
  ) => {
    try {
      const portfolioRef = doc(db, "portfolios", portfolioId)
      const portfolioDoc = await getDoc(portfolioRef)

      if (!portfolioDoc.exists()) {
        console.log(`Portfolio ${portfolioId} not found`)
        return false
      }

      const portfolio = portfolioDoc.data()
      const members = portfolio.members || []

      // Find the member in the portfolio
      const memberIndex = members.findIndex((m: Member) => m.userId === userId)

      if (memberIndex !== -1) {
        // Update the member's status
        const updatedMembers = [...members]
        updatedMembers[memberIndex] = {
          ...updatedMembers[memberIndex],
          status: status,
        }

        // Update the portfolio
        await updateDoc(portfolioRef, {
          members: updatedMembers,
        })

        console.log(
          `Updated member ${userId} status to ${status} in portfolio ${portfolioId}`
        )
        return true
      } else {
        console.log(`Member ${userId} not found in portfolio ${portfolioId}`)
        return false
      }
    } catch (error) {
      console.error("Error updating member status:", error)
      return false
    }
  }

  // Helper function to get user ID by email
  const getUserIdByEmail = async (email: string) => {
    try {
      const usersRef = collection(db, "users")
      const q = query(usersRef, where("email", "==", email))
      const querySnapshot = await getDocs(q)

      if (!querySnapshot.empty) {
        return querySnapshot.docs[0].id
      }

      return null
    } catch (error) {
      console.error("Error getting user ID:", error)
      return null
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (password !== confirmPassword) {
      toast({
        title: "Passwords do not match",
        description: "Please ensure both password fields are the same.",
        variant: "destructive",
      })
      return
    }

    if (!resetCode) {
      navigate("/reset-password")
      return
    }

    setIsLoading(true)

    try {
      // Get the email associated with the reset code
      const auth = getAuth()
      const email = await verifyPasswordResetCode(auth, resetCode)

      // Reset the password
      const result = await resetPasswordWithCode(resetCode, password)

      if (result.success) {
        // Get the user ID
        const userId = await getUserIdByEmail(email)

        if (userId) {
          // Check for pending invitations
          const invitation = await getInvitationByEmail(email, "invited")

          if (invitation && invitation.portfolioId) {
            // Update invitation status
            await updateInvitationStatus(email, "accepted")

            // Update portfolio membership status
            await updateMemberStatusInPortfolio(
              userId,
              invitation.portfolioId,
              "active"
            )

            toast({
              title: "Account Setup Complete",
              description:
                "Your password has been set and you've been added to the portfolio.",
            })
          } else {
            toast({
              title: "Password Reset",
              description: "Your password has been successfully reset.",
            })
          }
        } else {
          toast({
            title: "Password Reset",
            description: "Your password has been successfully reset.",
          })
        }

        navigate("/login")
      } else {
        toast({
          title: "Reset Failed",
          description:
            result.error || "Failed to reset password. Please try again.",
          variant: "destructive",
        })
      }
    } catch (error) {
      console.error("Password reset error:", error)
      toast({
        title: "Reset Failed",
        description:
          error instanceof Error
            ? error.message
            : "An unexpected error occurred. Please try again.",
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  if (!isCodeValid) {
    return null // or a loading spinner
  }

  return (
    <AuthLayout>
      <Card className="w-full">
        <CardHeader className="space-y-1">
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="icon"
              className="mr-2"
              onClick={() => navigate("/login")}
            >
              <ArrowLeft size={18} />
            </Button>
            <CardTitle className="text-2xl font-bold">Reset Password</CardTitle>
          </div>
          <CardDescription>
            Enter a new password for your account
          </CardDescription>
        </CardHeader>

        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter new password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10"
                  required
                  minLength={8}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10"
                  required
                  minLength={8}
                />
              </div>
            </div>
          </CardContent>
          <CardFooter>
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? "Resetting..." : "Reset Password"}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </AuthLayout>
  )
}

export default PasswordResetConfirm
